import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { useDiscountSpreadSettings } from "../../utils/portfolio-risk-concentration/api/discountSettings";

const BASE = "/risk-concentrations";

/**
 * First-run gate: every route except Manage Capital itself (and Manage Access, carved out of
 * the outer entitlement gate for the same reason) redirects here until Discount & Spread
 * Settings is genuinely set up. Unlike RequireTier (access-control.tsx), which gates on
 * entitlement and is synchronous, this gates on configuration completeness and needs an async
 * loading state — a different concern, deliberately kept in its own file.
 *
 * DCB never blocks anything: it already has a real default (ORIGINAL_CONTRACT_RATE), so it's
 * never genuinely "unconfigured". The gate releases once every FMV-eligible pool
 * (fmvRate !== null) has fmvRate > 0 — i.e. no pool that participates in FMV pricing is
 * sitting on a completely empty spread grid. See discountSettings.ts's own docs on why
 * fmvRate is a cell COUNT, not a percentage, and why 0 is a real, distinct "not configured
 * yet" state rather than an off sentinel.
 *
 * No persisted "have I seen this" flag anywhere: this always reflects real, current settings
 * data, so it stops blocking the instant the grid actually has pricing, with nothing to reset
 * or expire.
 */
export function RequireDiscountSpreadConfigured({ children }: { children: ReactNode }) {
  // Every row must be checked, not one page's worth -- 100 comfortably covers this table's
  // real size (one row per LIVE investor code, a few dozen today).
  const { data, isLoading } = useDiscountSpreadSettings({ limit: 100, offset: 0 });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Checking setup…</p>
      </div>
    );
  }

  // No data (query failed, or sql/33_ not applied on this environment yet): let it through
  // rather than hard-locking the product over an ambiguous state. The gate only actively
  // blocks on a real, positive "still empty" signal, never on the absence of one.
  const settings = data?.settings ?? [];
  const configured =
    settings.length === 0 || settings.every((s) => s.fmvRate === null || s.fmvRate > 0);

  if (!configured) {
    return <Navigate to={`${BASE}/manage-capital`} replace />;
  }

  return <>{children}</>;
}
